import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { eventTypeAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

const emptyForm = { name: '', description: '' };

const EventTypes = () => {
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', search: '' });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const search = query.search || '';

  const { data, isLoading } = useQuery(
    ['event-types', page, limit, search],
    () => eventTypeAPI.list({ page, limit, search: search || undefined })
  );
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: items.length };

  const createMutation = useMutation((payload) => eventTypeAPI.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['event-types']);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const updateMutation = useMutation(({ id, payload }) => eventTypeAPI.update(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['event-types']);
      setEditing(null);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const deleteMutation = useMutation((id) => eventTypeAPI.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(['event-types'])
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (editing) {
      updateMutation.mutate({
        id: editing,
        payload: { name: form.name.trim(), description: form.description?.trim() || '' }
      });
    } else {
      createMutation.mutate({
        name: form.name.trim(),
        description: form.description?.trim() || '',
        isActive: true,
        order: 0
      });
    }
  };

  const onEdit = (item) => {
    setEditing(item.id);
    setShowForm(true);
    setForm({
      name: item.name || '',
      description: item.description || ''
    });
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Event Types"
        subtitle="Manage event type options for trek packages and website filters."
        actions={
          !showForm ? (
            <button
              className="module-btn"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setShowForm(true);
              }}
            >
              Add Event Type
            </button>
          ) : null
        }
      />

      {showForm ? (
        <form className="module-card module-form-grid" onSubmit={onSubmit} style={{ maxWidth: 560 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required placeholder="e.g. Expedition" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea rows="3" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Short description..." />
          </div>
          <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem', gridColumn: '1 / -1' }}>
            <button className="module-btn" type="submit" disabled={createMutation.isLoading || updateMutation.isLoading}>
              {editing ? 'Update Event Type' : 'Create Event Type'}
            </button>
            <button
              type="button"
              className="module-btn secondary"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {!showForm ? (
        <FilterBar>
          <input
            type="text"
            className="module-filter-input"
            placeholder="Search event types..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
          />
        </FilterBar>
      ) : null}

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading event types...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No event types yet"
            description="Add an event type to use in package forms and web filters."
            actionLabel={!showForm ? 'Add Event Type' : undefined}
            onAction={
              !showForm
                ? () => {
                    setEditing(null);
                    setForm(emptyForm);
                    setShowForm(true);
                  }
                : undefined
            }
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td style={{ maxWidth: 360 }}>{item.description || '–'}</td>
                  <td className="module-actions">
                    <button className="module-link-btn" onClick={() => onEdit(item)}>Edit</button>
                    <button
                      className="module-link-btn danger"
                      onClick={() => {
                        if (window.confirm('Delete this event type?')) deleteMutation.mutate(item.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="module-list-pagination">
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <div className="module-list-pagination-btns">
                <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                <button type="button" className="module-btn secondary" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventTypes;
